/** @format */

import { ButtonGroup, Flex, FormControl, SimpleGrid } from "@chakra-ui/react";
import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import Card from "../../../components/card/Card";
import CustomButton from "../../../components/Common/CustomButton";
import CustomInput from "../../../components/Common/CustomInput";
import CustomSelect from "../../../components/Common/CustomInput/CustomSelect";
import IsLoading from "../../../components/Common/IsLoading";
import { StatusData } from "../Category/Create";
import { actions as groupActions } from "../group/redux/slice";
import FileUploadBox from "../product/FileUploadBox";
import { selectAllGroupSubCategoryList } from "../sub-category/redux/selector";
import { actions as subCategoryActions } from "../sub-category/redux/slice";
import Items from "./items";
import {
  selectButtonLoading,
  selectFormData,
  selectLoading,
} from "./redux/selector";
import { actions } from "./redux/slice";
import { IItems, PackageTypeEnum } from "./types";
import { useTranslation } from "react-i18next";
import { selectGroupPackage } from "../group/redux/selector";

const Create = () => {
  const { t } = useTranslation();
  const params = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const form = useSelector(selectFormData);
  const subCategory = useSelector(selectAllGroupSubCategoryList);
  const loader = useSelector(selectLoading);
  const loading = useSelector(selectButtonLoading);
  const groupList = useSelector(selectGroupPackage);

  React.useEffect(() => {
    dispatch(subCategoryActions.doGetAllGroupSubCategoryList());
    return () => {};
  }, []);
  React.useEffect(() => {
    if (params.id) {
      dispatch(actions.doGetById(params.id));
      dispatch(groupActions.doGetGroupPackageById(params.id || ""));
    }
    return () => {};
  }, [params.id]);

  useEffect(() => {
    if (form._id && groupList.length > 0) {
      dispatch(
        actions.loadItemsInit(
          groupList.map((x) => {
            return { groupId: x._id, productId: "" } as IItems;
          })
        )
      );
    }

    return () => {};
  }, [form._id, groupList]);

  const handleFieldChange = (evt: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = evt.target;
    dispatch(actions.updateFormValue({ key: name, value: value }));
  };

  const handleCancel = () => {
    dispatch(actions.clearFrom());
    navigate(-1);
  };
  const handleChangeUploadField = (name: string, val: any) => {
    dispatch(actions.updateImagesFormValue({ name: name, value: val }));
  };
  const handleChangeFeaturedImage = (val: string) => {
    dispatch(actions.updateFeaturedImageFormValue(val));
  };
  const handleDeleteFile = (name: string, index: number) => {
    dispatch(actions.doRemoveImages({ name: name, index: index }));
  };

  const handleSubmit = () => {
    if (form?._id) {
      dispatch(
        actions.doUpdate({
          callback() {
            dispatch(actions.clearFrom());
            navigate(-1);
          },
        })
      );
    } else {
      dispatch(
        actions.doCreate({
          callback() {
            navigate(-1);
            dispatch(actions.clearFrom());
          },
        })
      );
    }
  };

  const setStatusDisable = () => {
    if (
      form.type === PackageTypeEnum.Non_Customizable &&
      form.items.length === 0
    ) {
      return true;
    }
    if (
      form.items.findIndex((item) => item?.productId === "") !== -1 &&
      form.type === PackageTypeEnum.Non_Customizable
    ) {
      return true;
    }
    if (
      form.type === PackageTypeEnum.Non_Customizable &&
      form.items.filter(
        (x) => groupList.findIndex((y) => y._id === x.groupId) !== -1
      ).length !== groupList.length
    ) {
      return true;
    }
    return false;
  };

  return (
    <Flex pt={{ base: "130px", md: "80px", xl: "80px" }}>
      <Card>
        {loader ? (
          <IsLoading />
        ) : (
          <FormControl>
            <SimpleGrid columns={[1, 1, 2, 3]} spacingX={5}>
              <CustomInput
                name={"title"}
                value={form?.title}
                handleChange={handleFieldChange}
                placeholder={`${t("product.form.titleCode")}`}
                title={`${t("product.form.title")}`}
              />
              <CustomInput
                name={"code"}
                value={form?.code}
                handleChange={handleFieldChange}
                placeholder={`${t("product.form.enterCode")}`}
                title={`${t("product.form.code")}`}
              />

              <CustomInput
                name={"price"}
                value={form?.price}
                handleChange={handleFieldChange}
                placeholder={`${t("product.form.enterPrice")}`}
                title={`${t("product.form.price")}`}
                type='number'
              />

              <CustomInput
                name={"taxRate"}
                value={form?.taxRate}
                handleChange={handleFieldChange}
                placeholder={`${t("product.form.enterTaxRate")}`}
                title={`${t("product.form.taxRate")}`}
                type='number'
              />
              <CustomInput
                name={"shipmentCapacity"}
                value={form?.shipmentCapacity.toString()}
                handleChange={handleFieldChange}
                placeholder={`${t("product.form.enterShipmentCapacity")}`}
                title={`${t("product.form.shipmentCapacity")}`}
                type='number'
              />

              <CustomSelect
                name={"type"}
                value={form?.type}
                handleChange={handleFieldChange}
                title={`${t("product.form.type")}`}
                data={TypeData}
              />
              <CustomSelect
                name={"subCategoryId"}
                value={form?.subCategoryId}
                handleChange={handleFieldChange}
                title={`${t("product.form.subCategory")}`}
                data={subCategory}
                isFromApi
              />

              <CustomSelect
                name={"status"}
                value={form?.status}
                handleChange={handleFieldChange}
                title={`${t("common.form.status")}`}
                data={StatusData}
                disabled={setStatusDisable()}
              />
              <div />
            </SimpleGrid>
            <FileUploadBox
              handleChangeFeaturedImage={handleChangeFeaturedImage}
              handleChangeUploadField={handleChangeUploadField}
              handleDeleteFile={handleDeleteFile}
              form={form}
            />
            {form._id && <Items />}

            <SimpleGrid columns={1} spacing={10}>
              <Flex flexDirection='column'>
                <ButtonGroup justifyContent={"flex-end"} mt={8}>
                  <CustomButton
                    handleClick={handleCancel}
                    title={t("common.button.cancel")}
                    loading={false}
                    variant='light'
                    sx={{ marginRight: "20px" }}
                  />
                  <CustomButton
                    handleClick={handleSubmit}
                    title={t("common.button.save")}
                    loading={loading}
                    disabled={!form?.title}
                    sx={{ marginLeft: "20px" }}
                  />
                </ButtonGroup>
              </Flex>
            </SimpleGrid>
          </FormControl>
        )}
      </Card>
    </Flex>
  );
};

export default Create;
export const TypeData = [
  { _id: PackageTypeEnum.Customizable, value: PackageTypeEnum.Customizable },
  {
    _id: PackageTypeEnum.Non_Customizable,
    value: PackageTypeEnum.Non_Customizable,
  },
];
